import _slicedToArray from "/builds/superbcamping/web-superbcamping/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import storage from './storage';
import { unstable_batchedUpdates } from 'react-dom';
import { useRef, useMemo, useEffect, useReducer, useCallback, useLayoutEffect } from 'react'; // `activeHooks` holds all active hooks. we use the array to update all hooks with the same key —
// calling `setValue` of one hook triggers an update for all other hooks with the same key

var activeHooks = new Set();
export default function useSessionStorageState(key, options) {
  var defaultValue = options === null || options === void 0 ? void 0 : options.defaultValue; // SSR support

  if (typeof window === 'undefined') {
    return [defaultValue, function () {}, {
      isPersistent: true,
      removeItem: function removeItem() {}
    }];
  } // eslint-disable-next-line react-hooks/rules-of-hooks


  return useBrowserSessionStorageState(key, defaultValue, (options === null || options === void 0 ? void 0 : options.ssr) === true, (options === null || options === void 0 ? void 0 : options.storageSync) !== false);
}

function useBrowserSessionStorageState(key, defaultValue, ssr, storageSync) {
  var initialDefaultValue = useRef(defaultValue).current; // `id` changes every time a change in the `sessionStorage` occurs

  var _useReducer = useReducer(function (number) {
    return number + 1;
  }, 0),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      id = _useReducer2[0],
      forceUpdate = _useReducer2[1];

  var updateHooks = useCallback(function () {
    unstable_batchedUpdates(function () {
      // - it fixes "🐛 `setValue()` during render doesn't work":
      //   https://github.com/astoilkov/use-local-storage-state/issues/43
      forceUpdate();

      var _iterator = _createForOfIteratorHelper(activeHooks),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var hook = _step.value;

          if (hook.key === key) {
            hook.forceUpdate();
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    });
  }, [key]);
  var setState = useCallback(function (newValue) {
    storage.set(key, newValue instanceof Function ? newValue(storage.get(key, initialDefaultValue)) : newValue);
    updateHooks();
  }, [key, updateHooks, initialDefaultValue]); // - syncs change across tabs, windows, iframe's
  // - the `storage` event is called only in all tabs, windows, iframe's except the one that
  //   triggered the change

  useEffect(function () {
    if (!storageSync) {
      return undefined;
    }

    var onStorage = function onStorage(e) {
      if (e.storageArea === sessionStorage && e.key === key) {
        forceUpdate();
      }
    };

    window.addEventListener('storage', onStorage);
    return function () {
      return window.removeEventListener('storage', onStorage);
    };
  }, [key, storageSync]); // - adds this hook to the `activeHooks` array. see the `activeHooks` declaration above for a
  //   more detailed explanation

  useLayoutEffect(function () {
    var hook = {
      key: key,
      forceUpdate: forceUpdate
    };
    activeHooks.add(hook);
    return function () {
      activeHooks.delete(hook);
    };
  }, [key]); // - SSR support
  // - not inside a `useLayoutEffect` because this way we skip the calls to `useEffect()` and
  //   `useLayoutEffect()` for the first render (which also increases performance)
  // - inspired by: https://github.com/astoilkov/use-local-storage-state/pull/40
  // - related: https://github.com/astoilkov/use-local-storage-state/issues/39
  // - related: https://github.com/astoilkov/use-local-storage-state/issues/43

  var isFirstRenderRef = useRef(true);
  var isPossiblyHydrating = ssr && isFirstRenderRef.current;
  isFirstRenderRef.current = false;

  if (isPossiblyHydrating && (storage.data.has(key) || initialDefaultValue !== storage.get(key, initialDefaultValue))) {
    forceUpdate();
  } // initial issue: https://github.com/astoilkov/use-local-storage-state/issues/26
  // issues that were caused by incorrect initial and secondary implementations:
  // - https://github.com/astoilkov/use-local-storage-state/issues/30
  // - https://github.com/astoilkov/use-local-storage-state/issues/33


  if (initialDefaultValue !== undefined && !storage.data.has(key) && sessionStorage.getItem(key) === null) {
    storage.set(key, initialDefaultValue);
  }

  return useMemo(function () {
    return [isPossiblyHydrating ? initialDefaultValue : storage.get(key, initialDefaultValue), setState, {
      isPersistent: isPossiblyHydrating || !storage.data.has(key),
      removeItem: function removeItem() {
        storage.remove(key);
        updateHooks();
      }
    }];
  }, // disabling eslint warning for the following reasons:
  // - `id` is needed because when it changes that means the data in `sessionStorage` has
  //   changed and we need to update the returned value. However, the eslint rule wants us to
  //   remove the `id` from the dependencies array.
  // - `defaultValue` never changes so we can skip it and reduce package size
  // - `setState` changes when `key` changes so we can skip it and reduce package size
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [id, key]);
}